import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PatientPortalAuthProvider, usePatientPortalAuth } from "@/context/PatientPortalAuthContext";
import { LiveUpdatesProvider } from "@/context/LiveUpdatesContext";
import { TreatmentAlertListener } from "@/components/TreatmentAlertListener";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import { SITE_ROUTES } from "@/components/site/siteUtils";
import { hasMedicalAccess } from "@/lib/permissions";
import { lazy, Suspense } from "react";
import "@/App.css";

/* ── Eager: public site (critical for first paint) ─────────── */
import InstitutionalSite from "@/pages/InstitutionalSite";

/* ── Eager: auth pages (small, needed immediately) ─────────── */
import Login from "@/pages/Login";
import PasswordResetRequest from "@/pages/PasswordResetRequest";
import PasswordResetConfirm from "@/pages/PasswordResetConfirm";
import PatientPortalLogin from "@/pages/PatientPortalLogin";
import PatientPortalPasswordResetRequest from "@/pages/PatientPortalPasswordResetRequest";
import PatientPortalPasswordResetConfirm from "@/pages/PatientPortalPasswordResetConfirm";

/* ── Lazy: internal pages (only loaded when authenticated) ── */
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Medications = lazy(() => import("@/pages/Medications"));
const Batches = lazy(() => import("@/pages/Batches"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const ApplicationsToday = lazy(() => import("@/pages/ApplicationsToday"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const ApplicationsHistory = lazy(() => import("@/pages/ApplicationsHistory"));
const Movements = lazy(() => import("@/pages/Movements"));
const PurchaseNeeds = lazy(() => import("@/pages/PurchaseNeeds"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Users = lazy(() => import("@/pages/Users"));
const Reports = lazy(() => import("@/pages/Reports"));
const CashBook = lazy(() => import("@/pages/CashBook"));
const ProofQueue = lazy(() => import("@/pages/ProofQueue"));
const Backups = lazy(() => import("@/pages/Backups"));
const TreatmentsActive = lazy(() => import("@/pages/TreatmentsActive"));
const TreatmentsFinished = lazy(() => import("@/pages/TreatmentsFinished"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const Professionals = lazy(() => import("@/pages/Professionals"));
const Procedures = lazy(() => import("@/pages/Procedures"));
const Products = lazy(() => import("@/pages/Products"));
const Exams = lazy(() => import("@/pages/Exams"));
const ExamRequests = lazy(() => import("@/pages/ExamRequests"));
const ConsultationTypes = lazy(() => import("@/pages/ConsultationTypes"));
const MedicalCatalog = lazy(() => import("@/pages/MedicalCatalog"));
const PatientPortalHome = lazy(() => import("@/pages/PatientPortalHome"));
const PortalBanners = lazy(() => import("@/pages/PortalBanners"));
const PortalAccessManagement = lazy(() => import("@/pages/PortalAccessManagement"));
const SiteCms = lazy(() => import("@/pages/SiteCms"));

/* ── Loading fallback ──────────────────────────────────────── */
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center text-[#0B2230]">Carregando…</div>
);

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && !(roles.includes("medico") && hasMedicalAccess(user.role))) return <Navigate to="/consultas-agenda" replace />;
  return <Layout><Suspense fallback={<PageLoader />}>{children}</Suspense></Layout>;
}

function PatientPortalProtected({ children }) {
  const { patient, loading } = usePatientPortalAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#0B2230]" data-testid="patient-portal-loading">Carregando…</div>;
  if (!patient) return <Navigate to="/area-paciente/login" replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function WorkspaceApp() {
  return (
    <AuthProvider>
      <PatientPortalAuthProvider>
      <LiveUpdatesProvider>
        <TreatmentAlertListener />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-senha" element={<PasswordResetRequest />} />
          <Route path="/redefinir-senha" element={<PasswordResetConfirm />} />
          <Route path="/area-paciente/login" element={<PatientPortalLogin />} />
          <Route path="/area-paciente/esqueci-senha" element={<PatientPortalPasswordResetRequest />} />
          <Route path="/area-paciente/redefinir-senha" element={<PatientPortalPasswordResetConfirm />} />
          <Route path="/area-paciente" element={<PatientPortalProtected><PatientPortalHome /></PatientPortalProtected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/configuracoes/site-institucional" element={<Protected><SiteCms /></Protected>} />
          <Route path="/medicamentos" element={<Protected><Medications /></Protected>} />
          <Route path="/lotes" element={<Protected><Batches /></Protected>} />
          <Route path="/pacientes" element={<Protected><Patients /></Protected>} />
          <Route path="/pacientes/:id" element={<Protected><PatientDetail /></Protected>} />
          <Route path="/aplicacoes/hoje" element={<Protected><ApplicationsToday /></Protected>} />
          <Route path="/aplicacoes/agenda" element={<Protected><Schedule /></Protected>} />
          <Route path="/consultas-agenda" element={<Protected><Agenda /></Protected>} />
          <Route path="/profissionais" element={<Protected><Professionals /></Protected>} />
          <Route path="/procedimentos" element={<Protected><Procedures /></Protected>} />
          <Route path="/produtos" element={<Protected><Products /></Protected>} />
          <Route path="/exames" element={<Protected><Exams /></Protected>} />
          <Route path="/pedidos-exames" element={<Protected><ExamRequests /></Protected>} />
          <Route path="/aplicacoes/historico" element={<Protected><ApplicationsHistory /></Protected>} />
          <Route path="/movimentacoes" element={<Protected><Movements /></Protected>} />
          <Route path="/necessidade-compra" element={<Protected><PurchaseNeeds /></Protected>} />
          <Route path="/fornecedores" element={<Protected><Suppliers /></Protected>} />
          <Route path="/usuarios" element={<Protected><Users /></Protected>} />
          <Route path="/configuracoes/tipos-consulta" element={<Protected><ConsultationTypes /></Protected>} />
          <Route path="/configuracoes/banners-area-paciente" element={<Protected><PortalBanners /></Protected>} />
          <Route path="/configuracoes/prontuario/:catalog" element={<Protected><MedicalCatalog /></Protected>} />
          <Route path="/relatorios" element={<Protected><Reports /></Protected>} />
          <Route path="/livro-caixa" element={<Protected><CashBook /></Protected>} />
          <Route path="/comprovantes" element={<Protected><ProofQueue /></Protected>} />
          <Route path="/acessos-portal" element={<Protected roles={["admin", "medico", "secretaria"]}><PortalAccessManagement /></Protected>} />
          <Route path="/backup" element={<Protected><Backups /></Protected>} />
          <Route path="/tratamentos-ativos" element={<Protected><TreatmentsActive /></Protected>} />
          <Route path="/tratamentos-finalizados" element={<Protected><TreatmentsFinished /></Protected>} />
          <Route path="*" element={<Navigate to="/consultas-agenda" replace />} />
        </Routes>
      </LiveUpdatesProvider>
      </PatientPortalAuthProvider>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

function App() {
  return <BrowserRouter><Routes>
    {SITE_ROUTES.map((path) => <Route key={path} path={path} element={<InstitutionalSite />} />)}
    <Route path="/servicos/:entryId" element={<InstitutionalSite />} />
    <Route path="/blog/:entryId" element={<InstitutionalSite />} />
    <Route path="*" element={<WorkspaceApp />} />
  </Routes></BrowserRouter>;
}

export default App;
